import moment from 'moment'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { Address, MatchedEntity } from '../@types/GetEntitiesDetails'
import { NPANavigationClientContextProps } from '../@types/NPANavitationClientContext'
import { NPALeadsContextProps } from '../@types/LeadsInfo'
import { MatchedEntity as MatchedEntityPolicyHolder } from '../@types/PolicyHolderDetails'
import { GetAdditionalEntityDetailsClientResponse } from '../@types/getAdditionalEntityDetails'
import { EntityPayload } from '../Types/EntityDetails'

const initialEntity = {
  address: {
    postalCode: null,
    addressPointer: null,
  },
  contactDetails: {
    phonePointer: null,
    mailingAddressPointer: null,
  },
  entityId: null,
  name: null,
  birthDate: null,
  initialDate: null,
  endDate: null,
  idExternal: null,
  role: null,
  payer: true,
}

export const INSTALLMENT_DEFAULT = 'ANN'
export type TypeOfPriceInstallment = 'ANN' | 'BNN' | 'QTL' | 'MTL' | string

type CurrentPack = { [key: string]: string }

export const TODAY = moment().format('DD-MM-YYYY')

type Store = {
  initialDate: string
  endDate: string
  setInitialDate: (value: string) => void
  setEndDate: (value: string) => void
  entity: EntityPayload
  setEntity: (value: EntityPayload) => void
  entitySelected: MatchedEntity | MatchedEntityPolicyHolder | null
  entitySelectedAddress: Address | null
  setEntitySelectedAddress: (value: Address) => void
  setEntitySelected: (
    value: MatchedEntity | MatchedEntityPolicyHolder | null,
  ) => void
  resetEntityData: () => void
  updateEntitySelected: (
    value: GetAdditionalEntityDetailsClientResponse,
  ) => void
  breeds: any[]
  setBreeds: (value: any) => void
  price: string
  setPrice: (value: string) => void
  totalPricesObjects: any
  resetTotalPricesObjects: () => void
  totalPrice: any
  setTotalPrice: (value: any) => void
  quoteId: string
  setQuoteId: (value: string) => void
  typeOfPrice: TypeOfPriceInstallment
  setTypeOfPrice: (value: string) => void
  columnSelected: string
  setColumnSelected: (value: string) => void
  isFullStop: boolean
  setIsFullStop: (value: boolean) => void
  isClientContext: boolean
  setIsClientContext: (value: boolean) => void
  NPANavigationClientContext: NPANavigationClientContextProps | null
  setNPANavigationClientContext: (
    value: NPANavigationClientContextProps,
  ) => void
  isNPANavigationClientContext: boolean
  NPALeadsContext: NPALeadsContextProps | null
  setNPALeadsContext: (
    value: NPALeadsContextProps,
  ) => void
  isNPALeadsContext: boolean
  setIsNPALeadsContext: (value: boolean) => void
  setIsNPANavigationClientContext: (value: boolean) => void
  isAcceptedConditions: boolean
  setIsAcceptedConditions: (value: boolean) => void
  isRecalculate: boolean
  setIsRecalculate: (isRecalculate: boolean) => void
  hasRecalculate: {
    [key: string]: string[]
  } | null
  setHasRecalculate: (animalId: string, hasRecalculate: string) => void
  resetHasRecalculate: () => void
  currentPack: CurrentPack
  setCurrentPack: (currentPack: CurrentPack) => void
  resetToRPS: () => void
  hasGenerateProposal: boolean
  setHasGenerateProposal: (value: boolean) => void
  ownerFiscalNumber: string
  setOwnerFiscalNumber: (value: string) => void
  showSelectedEntity: boolean
  setShowSelectedEntity: (value: boolean) => void
  hasUpdateCheckbox: boolean
  setHasUpdateCheckbox: (value: boolean) => void
}

export const useSimulatorStore = create<Store>()(
  devtools(
    (set, get) => ({
      initialDate: TODAY,
      endDate: moment(TODAY, 'DD-MM-YYYY')
        .add(1, 'year')
        .subtract(1, 'days')
        .format(),
      setInitialDate: (initialDate) =>
        set(() => {
          const endDate = moment(initialDate, 'DD-MM-YYYY')
            .add(1, 'year')
            .subtract(1, 'days')
            .format()

          return { initialDate, endDate }
        }),
      setEndDate: (endDate: string) => set({ endDate }),
      entity: initialEntity,
      setEntity: (value: EntityPayload) => set({ entity: value }),
      entitySelected: null,
      entitySelectedAddress: null,
      setEntitySelected: (entitySelected) => set({ entitySelected }),
      setEntitySelectedAddress: (entitySelectedAddress) =>
        set({ entitySelectedAddress }),
      updateEntitySelected: (additional) =>
        set((state) => {
          const cloneEntityDetails = JSON.parse(
            JSON.stringify(state.entitySelected),
          )
          return {
            entitySelected: {
              ...cloneEntityDetails,
              additional,
            },
          }
        }),
      breeds: [],
      setBreeds: (value: any) => set({ breeds: value }),
      price: '',
      totalPricesObjects: {},
      setPrice: (value: any) => set({ price: value }),
      resetTotalPricesObjects: () => set({ totalPricesObjects: {} }),
      totalPrice: '',
      setTotalPrice: (value: any) =>
        set((state) => {
          return {
            totalPricesObjects: {
              ...state.totalPricesObjects,
              ...value,
            },
          }
        }),
      quoteId: '',
      setQuoteId: (value: string) => set({ quoteId: value }),
      typeOfPrice: INSTALLMENT_DEFAULT,
      setTypeOfPrice: (value: string) => set({ typeOfPrice: value }),
      columnSelected: '',
      setColumnSelected: (columnSelected) => set({ columnSelected }),
      isClientContext: false,
      setIsClientContext: (isClientContext) => set({ isClientContext }),
      isFullStop: false,
      setIsFullStop: (value) => set({ isFullStop: value }),
      NPANavigationClientContext: null,
      setNPANavigationClientContext: (NPANavigationClientContext) =>
        set({ NPANavigationClientContext }),
      isNPANavigationClientContext: false,
      setIsNPANavigationClientContext: (value) =>
        set({ isNPANavigationClientContext: value }),
      NPALeadsContext: null,
      setNPALeadsContext: (NPALeadsContext) =>
        set({ NPALeadsContext }),
      isNPALeadsContext: false,
      setIsNPALeadsContext: (value) =>
        set({ isNPANavigationClientContext: value }),
      isAcceptedConditions: false,
      setIsAcceptedConditions: (value) => set({ isAcceptedConditions: value }),
      hasGenerateProposal: false,
      setHasGenerateProposal: (value) => set({ hasGenerateProposal: value }),
      showSelectedEntity: false,
      setShowSelectedEntity: (value) => set({ showSelectedEntity: value }),
      isRecalculate: false,
      setIsRecalculate: (isRecalculate) => set({ isRecalculate }),
      hasRecalculate: null,
      resetHasRecalculate: () => set({ hasRecalculate: null }),
      setHasRecalculate: (animalId, hasRecalculate) =>
        set(() => {
          const hasRecalculateState = get().hasRecalculate

          if (hasRecalculateState?.[animalId]?.includes(hasRecalculate)) {
            return {
              hasRecalculate: {
                ...hasRecalculateState,
                [animalId]: hasRecalculateState?.[animalId]?.filter(
                  (item) => item !== hasRecalculate,
                ),
              },
            }
          }

          const newData = {
            ...hasRecalculateState,
            [animalId]: [
              ...(hasRecalculateState?.[animalId] || []),
              hasRecalculate,
            ],
          }

          return { hasRecalculate: newData }
        }),
      currentPack: {},
      setCurrentPack: (currentPack) =>
        set((state) => {
          return {
            currentPack: {
              ...state.currentPack,
              ...currentPack,
            },
          }
        }),
      resetToRPS: () =>
        set({ currentPack: {}, typeOfPrice: 'ANN', columnSelected: '' }),
      ownerFiscalNumber: '',
      setOwnerFiscalNumber: (ownerFiscalNumber) => set({ ownerFiscalNumber }),
      hasUpdateCheckbox: false,
      setHasUpdateCheckbox: (value) => set({ hasUpdateCheckbox: value }),
      resetEntityData: () =>
        set({
          entity: initialEntity,
          entitySelected: null,
          entitySelectedAddress: null,
        }),
    }),
    { name: 'SimulatorStore' },
  ),
)
